import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { AwardBiddersDialogComponent } from '../award-bidders-dialog/award-bidders-dialog.component';
import { ExtendBidClosingDialogComponent } from '../extend-bid-closing-dialog/extend-bid-closing-dialog.component';
import { CloseBidDialogComponent } from '../close-bid-dialog/close-bid-dialog.component';
import { InvitationsListDialogComponent } from '../invitations-list-dialog/invitations-list-dialog.component';

export type ViewMode = 'individual' | 'comparison';

@Component({
  selector: 'app-view-bids-page',
  templateUrl: './view-bids-page.component.html',
  styleUrls: ['./view-bids-page.component.css']
})
export class ViewBidsPageComponent implements OnInit {
  data: any = null;
  loading = true;
  ranking: any[] = [];
  rankingMeta: any = null;
  rankingLoading = false;
  replyTexts: { [key: number]: string } = {};
  invitationId: number | null = null;
  viewMode: ViewMode = 'individual';
  /** When true, hide rows where all bidders have identical values */
  showDifferencesOnly = false;
  /** Collapsible Bill of Quantities section - expanded by default */
  comparisonSectionExpanded = true;
  reminderSending = false;
  extendSending = false;
  closeBidSending = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('invitationId');
    if (id) {
      this.invitationId = +id;
      this.loadBids();
    } else {
      this.toastr.error('Invalid invitation');
      this.loading = false;
    }
  }

  loadBids(): void {
    if (!this.invitationId) return;
    this.apiService.getBiddingResponsesByInvitation(this.invitationId).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.data = res.data;
          this.loadRanking();
        } else {
          this.toastr.error(res.error || 'Failed to load bids');
        }
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error(err.error?.error || 'Failed to load bids');
        this.loading = false;
      }
    });
  }

  loadRanking(): void {
    if (!this.invitationId) return;
    this.rankingLoading = true;
    this.apiService.getVendorRankingByInvitation(this.invitationId).subscribe({
      next: (res: any) => {
        this.ranking = res?.data?.rankings || [];
        this.rankingMeta = res?.data?.invitation;
        this.rankingLoading = false;
      },
      error: () => {
        this.rankingLoading = false;
      },
    });
  }

  goToVendorRanking(): void {
    if (this.invitationId) {
      void this.router.navigate(['/home/vendor-ranking'], {
        queryParams: { invitation_id: this.invitationId },
      });
    }
  }

  formatCurrency(amount: number, currency: string = 'INR'): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  }

  updateBidStatus(bid: any, status: 'shortlisted' | 'rejected'): void {
    if (!this.invitationId) return;
    bid.updating = true;
    this.apiService.updateBidStatus(this.invitationId, bid.id, status).subscribe({
      next: (res: any) => {
        if (res.success) {
          bid.status = res.data.status;
          bid.updating = false;
          this.toastr.success(status === 'shortlisted' ? 'Bid approved' : 'Bid declined');
        }
      },
      error: (err) => {
        bid.updating = false;
        this.toastr.error(err.error?.error || 'Failed to update bid status');
      }
    });
  }

  replyToQuery(q: any): void {
    const replyText = this.replyTexts[q.id];
    if (!replyText?.trim() || !this.invitationId) return;
    this.apiService.replyToBiddingQuery(this.invitationId, q.id, replyText.trim()).subscribe({
      next: (res: any) => {
        if (res.success) {
          q.reply_text = res.data.reply_text;
          q.replied_at = res.data.replied_at;
          this.replyTexts[q.id] = '';
          this.toastr.success('Reply sent');
        }
      },
      error: (err) => this.toastr.error(err.error?.error || 'Failed to send reply')
    });
  }

  hasApprovedBid(): boolean {
    return this.data?.responses?.some((b: any) => b.status === 'shortlisted') ?? false;
  }

  goToDeviationReport(): void {
    if (this.invitationId) {
      this.router.navigate(['/home/boq-bidding/deviation-report', this.invitationId]);
    }
  }

  goToSurvey(): void {
    const projectId = this.data?.invitation?.project_id;
    if (projectId) {
      this.router.navigate(['/home/survey-list'], { queryParams: { project_id: projectId } });
    } else {
      this.router.navigate(['/home/survey-list']);
    }
  }

  goToWorkBreakdownStructure(): void {
    const projectId = this.data?.invitation?.project_id;
    if (projectId) {
      this.router.navigate(['/home/work-breakdown-structure', projectId]);
    } else {
      this.toastr.error('Project not linked to this invitation');
    }
  }

  goToResourceMobilization(): void {
    const projectId = this.data?.invitation?.project_id;
    if (projectId) {
      this.router.navigate(['/home/labour-mobilization/mobilization-plans'], { queryParams: { project_id: projectId } });
    } else {
      this.router.navigate(['/home/labour-mobilization/mobilization-plans']);
    }
  }

  canInviteMore(): boolean {
    const inv = this.data?.invitation;
    if (!inv || inv.status !== 'active') return false;
    if (inv.bid_closing_at && new Date(inv.bid_closing_at).getTime() < Date.now()) return false;
    return !!inv.boq_id;
  }

  openInviteMoreDialog(): void {
    const inv = this.data?.invitation;
    if (!this.canInviteMore() || !inv?.boq_id) {
      this.toastr.error('Cannot invite more bidders on this invitation');
      return;
    }
    const ref = this.dialog.open(InvitationsListDialogComponent, {
      width: '760px',
      maxWidth: '94vw',
      autoFocus: false,
      data: {
        boq_id: inv.boq_id,
        boq_code: inv.boq_code,
        boq_name: inv.boq_name,
        focusInviteMore: true
      }
    });
    ref.afterClosed().subscribe(() => this.loadBids());
  }

  sendBidReminder(): void {
    if (!this.invitationId || this.reminderSending) return;
    if ((this.data?.stats?.not_completed || 0) === 0) {
      this.toastr.info('No pending invitees to remind.');
      return;
    }
    this.reminderSending = true;
    this.apiService.sendBidReminder(this.invitationId).subscribe({
      next: (res: any) => {
        this.reminderSending = false;
        if (res.success) {
          this.toastr.success(res.message || 'Reminder emails sent.');
          this.loadBids();
        } else {
          this.toastr.error(res.error || 'Failed to send reminder');
        }
      },
      error: (err) => {
        this.reminderSending = false;
        this.toastr.error(err.error?.error || 'Failed to send reminder');
      }
    });
  }

  openExtendClosingDialog(): void {
    if (!this.invitationId || this.extendSending) return;
    const ref = this.dialog.open(ExtendBidClosingDialogComponent, {
      width: '460px',
      data: {
        current_closing_at: this.data?.invitation?.bid_closing_at,
        project_name: this.data?.invitation?.project_name,
        boq_code: this.data?.invitation?.boq_code
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result?.new_bid_closing_at) return;
      this.extendSending = true;
      this.apiService.extendBidClosing(this.invitationId!, { new_bid_closing_at: result.new_bid_closing_at, message: result.message }).subscribe({
        next: (res: any) => {
          this.extendSending = false;
          if (res.success) {
            this.toastr.success(res.message || 'Closing date extended and invitees notified.');
            this.loadBids();
          } else {
            this.toastr.error(res.error || 'Failed to extend');
          }
        },
        error: (err) => {
          this.extendSending = false;
          this.toastr.error(err.error?.error || 'Failed to extend closing date');
        }
      });
    });
  }

  openCloseBidDialog(): void {
    if (!this.invitationId || this.closeBidSending) return;
    const ref = this.dialog.open(CloseBidDialogComponent, {
      width: '420px',
      data: {
        project_name: this.data?.invitation?.project_name,
        boq_code: this.data?.invitation?.boq_code
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (result === undefined) return;
      this.closeBidSending = true;
      this.apiService.closeBidByPlanner(this.invitationId!, { comments: result?.comments }).subscribe({
        next: (res: any) => {
          this.closeBidSending = false;
          if (res.success) {
            this.toastr.success(res.message || 'Bid closed and invitees notified.');
            this.loadBids();
          } else {
            this.toastr.error(res.error || 'Failed to close bid');
          }
        },
        error: (err) => {
          this.closeBidSending = false;
          this.toastr.error(err.error?.error || 'Failed to close bid');
        }
      });
    });
  }

  openAwardBiddersDialog(): void {
    const shortlisted = (this.data?.responses || []).filter(
      (b: any) => b.status === 'shortlisted' && b.bidder_type !== 'guest'
    );
    if (shortlisted.length === 0) {
      this.toastr.error('No approved bids from registered organizations to award. Approve at least one registered bidder first.');
      return;
    }
    const awardDialogRef = this.dialog.open(AwardBiddersDialogComponent, {
      width: '480px',
      data: {
        invitation_id: this.invitationId,
        shortlistedBids: shortlisted.map((b: any) => ({ id: b.id, org_name: b.org_name, bid_amount: b.bid_amount })),
        project_name: this.data?.invitation?.project_name,
        boq_code: this.data?.invitation?.boq_code
      }
    });
    awardDialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadBids();
      }
    });
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
  }

  /** Build comparison rows: one per BOQ item with estimated + each bidder's values. Uses first response's items; all should align by item_id. */
  getComparisonRows(): Array<{
    item_id: number;
    item_code: string;
    item_description: string;
    unit: string;
    est_qty: number;
    est_rate: number;
    est_amt: number;
    bidderValues: Array<{ qty: number; rate: number; amt: number; dev_pct: number; isLowest: boolean }>;
  }> {
    const responses = this.data?.responses || [];
    if (responses.length === 0) return [];

    const firstWithItems = responses.find((r: any) => r?.bid_data?.items?.length);
    const firstItems = firstWithItems?.bid_data?.items || [];
    const itemIds = [...new Set(firstItems.map((i: any) => i.item_id))];
    if (itemIds.length === 0) return [];

    return itemIds.map((itemId: any) => {
      const ref = firstItems.find((i: any) => i.item_id === itemId);
      const estQty = parseFloat(ref?.estimated_quantity ?? ref?.quantity) || 0;
      const estRate = parseFloat(ref?.estimated_unit_rate ?? ref?.bid_rate ?? ref?.unit_rate) || 0;
      const estAmt = estQty * estRate;

      const bidderValues = responses.map((bid: any) => {
        const bi = bid?.bid_data?.items?.find((i: any) => i.item_id === itemId);
        const qty = parseFloat(bi?.quantity ?? bi?.estimated_quantity) || 0;
        const rate = parseFloat(bi?.bid_rate ?? bi?.estimated_unit_rate ?? bi?.unit_rate) || 0;
        const amt = (qty || 0) * (rate || 0);
        const devPct = estAmt > 0 ? ((amt - estAmt) / estAmt) * 100 : 0;
        return { qty, rate, amt, dev_pct: devPct, isLowest: false };
      });

      const amounts = bidderValues.map((b:any) => b.amt);
      const minAmt = Math.min(...amounts);
      bidderValues.forEach((b:any, i:any) => {
        b.isLowest = amounts[i] === minAmt && minAmt < Infinity;
      });

      return {
        item_id: itemId,
        item_code: ref?.item_code || '',
        item_description: ref?.item_description || '',
        unit: ref?.unit || '',
        est_qty: estQty,
        est_rate: estRate,
        est_amt: estAmt,
        bidderValues,
        hasDifference: this.rowHasDifference(bidderValues, estAmt)
      };
    });
  }

  /** Returns true if row has meaningful differences:
   * - Multiple bidders: at least one bidder differs from others
   * - Single bidder: bid deviates from estimate (dev_pct non-zero) */
  rowHasDifference(bidderValues: Array<{ qty: number; rate: number; amt: number; dev_pct?: number }>, estAmt: number): boolean {
    if (bidderValues.length > 1) {
      const first = bidderValues[0];
      const sameQty = bidderValues.every((b) => Math.abs((b.qty || 0) - (first?.qty || 0)) < 1e-6);
      const sameRate = bidderValues.every((b) => Math.abs((b.rate || 0) - (first?.rate || 0)) < 1e-6);
      const sameAmt = bidderValues.every((b) => Math.abs((b.amt || 0) - (first?.amt || 0)) < 1e-6);
      return !(sameQty && sameRate && sameAmt);
    }
    if (bidderValues.length === 1 && estAmt > 0) {
      const dev = bidderValues[0]?.dev_pct ?? 0;
      return Math.abs(dev) > 0.01;
    }
    return false;
  }

  /** Comparison rows filtered by showDifferencesOnly toggle */
  getFilteredComparisonRows() {
    const rows = this.getComparisonRows();
    if (!this.showDifferencesOnly) return rows;
    return rows.filter((r:any) => r.hasDifference);
  }

  getEstimatedTotal(): number {
    const rows = this.getComparisonRows();
    return rows.reduce((s, r) => s + r.est_amt, 0);
  }

  getLowestBid(): { amount: number; bidder: string } | null {
    const responses = this.data?.responses || [];
    if (responses.length === 0) return null;
    const sorted = [...responses].sort((a, b) => (a.bid_amount || 0) - (b.bid_amount || 0));
    return { amount: sorted[0]?.bid_amount || 0, bidder: sorted[0]?.org_name || 'Unknown' };
  }

  getHighestBid(): { amount: number; bidder: string } | null {
    const responses = this.data?.responses || [];
    if (responses.length === 0) return null;
    const sorted = [...responses].sort((a, b) => (b.bid_amount || 0) - (a.bid_amount || 0));
    return { amount: sorted[0]?.bid_amount || 0, bidder: sorted[0]?.org_name || 'Unknown' };
  }

  getDeviationClass(devPct: number): string {
    if (devPct < 0) return 'deviation-lower';
    if (devPct > 0) return 'deviation-higher';
    return 'deviation-match';
  }

  getTotalDeviationPct(bid: any): number {
    const est = this.getEstimatedTotal();
    return est > 0 ? ((bid?.bid_amount || 0) - est) / est * 100 : 0;
  }
}
